import React, { useMemo, useState, useEffect, createRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useWindowSize } from '@folklore/hooks';
import { useSprings, animated as a } from '@react-spring/web';

import styles from '../../../../styles/blocks/employee/math-pills.module.scss';
import PlusIcon from '../../icons/Plus';
import EqualsIcon from '../../icons/Equals';

const propTypes = {
    title: PropTypes.string,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            title: PropTypes.string,
            text: PropTypes.string,
            color: PropTypes.string,
            image: PropTypes.string,
        }),
    ),
    className: PropTypes.string,
};

const defaultProps = {
    title: null,
    items: null,
    className: null,
};

// @TODO: unused. Delete?
const MathPills = ({ title, items, className }) => {
    const { height: offset } = useWindowSize();

    const [scroll, setScroll] = useState(0);

    const [springs, setSprings] = useSprings(
        items?.length,
        () => ({
            transform: 'translateY(-50px)',
            opacity: 0,
        }),
        [],
    );

    const refs = useMemo(() => Array.from({ length: springs.length }).map(() => createRef()), []);
    const operatorRefs = useMemo(
        () => Array.from({ length: springs.length }).map(() => createRef(false)),
        [],
    );

    useEffect(() => {
        const onScroll = () => {
            setScroll(window.scrollY);
        };
        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);
    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <div className={classNames([styles.inner])}>
                {title !== null ? <h4 className={styles.title}>{title}</h4> : null}

                {(springs || []).map((spring, i) => {
                    const {
                        label = null,
                        title: bubbleTitle = null,
                        text = null,
                        color = null,
                        image = null,
                    } = items[i] || {};

                    useEffect(() => {
                        setSprings((ind) => {
                            const { y: posY } = refs[ind].current.getBoundingClientRect();
                            const ratio = 1 - posY / offset;

                            if (ratio > 0.4) {
                                operatorRefs[ind] = true;
                            } else {
                                operatorRefs[ind] = false;
                            }

                            return {
                                transform: ratio > 0.3 ? 'translateY(0)' : 'translateY(-50px)',
                                opacity: ratio > 0.3 ? 1 : 0,
                            };
                        });
                    }, [scroll, offset]);

                    return (
                        <a.div
                            key={`item-${i + 1}`}
                            ref={refs[i]}
                            className={classNames([
                                styles.item,
                                {
                                    [styles.withoutBubble]: bubbleTitle === null && text === null,
                                },
                            ])}
                            style={spring}
                        >
                            {label !== null ? (
                                <div
                                    className={styles.label}
                                    style={{ backgroundColor: color !== null ? color : null }}
                                >
                                    <span>{label}</span>
                                </div>
                            ) : null}
                            {image !== null ? (
                                <img
                                    src={image}
                                    alt=""
                                    className={classNames([
                                        styles.image,
                                        {
                                            [styles.left]: i % 2 === 0,
                                            [styles.larger]: label === null,
                                        },
                                    ])}
                                    style={{ animationDelay: `${i * 0.2}s` }}
                                />
                            ) : null}
                            {bubbleTitle !== null || text !== null ? (
                                <div
                                    className={classNames([
                                        styles.bubbleContainer,
                                        { [styles.left]: i % 2 !== 0 },
                                    ])}
                                    style={{ animationDelay: `${i}s` }}
                                >
                                    <div
                                        className={styles.bubble}
                                        style={{
                                            color: color !== null ? color : null,
                                            borderColor: color !== null ? color : null,
                                        }}
                                    >
                                        {bubbleTitle !== null ? (
                                            <span className={styles.bubbleTitle}>
                                                {bubbleTitle}
                                            </span>
                                        ) : null}
                                        {text !== null ? (
                                            <p className={styles.text}>{text}</p>
                                        ) : null}
                                    </div>
                                    {/* <div
                                        className={styles.bubbleOutline}
                                        style={{
                                            borderColor: color !== null ? color : null,
                                            left: i % 2 === 0 ? '5px' : '-5px',
                                        }}
                                    /> */}
                                </div>
                            ) : null}
                            {i < items.length - 2 ? (
                                <PlusIcon
                                    color="#fff"
                                    circleColor="#222"
                                    className={classNames([styles.operatorIcon], {
                                        [styles.showOperator]: operatorRefs[i] === true,
                                    })}
                                />
                            ) : null}
                            {i === items.length - 2 ? (
                                <EqualsIcon
                                    className={classNames([styles.operatorIcon], {
                                        [styles.showOperator]: operatorRefs[i] === true,
                                    })}
                                />
                            ) : null}
                        </a.div>
                    );
                })}
            </div>
        </div>
    );
};

MathPills.propTypes = propTypes;
MathPills.defaultProps = defaultProps;

export default MathPills;
